import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Textarea } from '../ui/textarea';
import { ArrowLeft, ArrowRight, Heart, Sparkles, RotateCcw, Check, X, Play, Pause, ChevronRight, Maximize2, Minimize2, Volume2, Timer, RefreshCw } from 'lucide-react';
import { useSearchParams, useNavigate } from 'react-router-dom';

// Custom images for the chapter
const IMAGES = {
  hero: 'https://images.unsplash.com/photo-1499209974431-9dddcece7f88?w=800&q=80', // Freedom/choice
  transform: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=800&q=80', // Mountains/liberation
  gratitude: 'https://images.unsplash.com/photo-1518495973542-4542c06a5843?w=800&q=80', // Light through trees
};

export default function Chapter8Presentation() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const initialSection = parseInt(searchParams.get('section')) || 0;
  const [currentSection, setCurrentSection] = useState(initialSection);
  
  // Broadcast/presentation mode
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isPresentationMode, setIsPresentationMode] = useState(false);
  
  // Exercise states
  const [haveToItems, setHaveToItems] = useState([
    { id: 1, haveTo: '', needs: '', chooseTo: '', revealed: false },
    { id: 2, haveTo: '', needs: '', chooseTo: '', revealed: false },
    { id: 3, haveTo: '', needs: '', chooseTo: '', revealed: false },
    { id: 4, haveTo: '', needs: '', chooseTo: '', revealed: false },
    { id: 5, haveTo: '', needs: '', chooseTo: '', revealed: false },
  ]);
  
  // Timer for guided practice
  const [timerActive, setTimerActive] = useState(false);
  const [timerSeconds, setTimerSeconds] = useState(120); // 2 minutes per item
  const [currentItemIndex, setCurrentItemIndex] = useState(0);
  
  // Pre-loaded examples for broadcast mode
  const exampleItems = [
    { haveTo: "I have to go to work", needs: "Security, contribution, purpose", chooseTo: "I choose to go to work because I value security and feeling purposeful" },
    { haveTo: "I have to exercise", needs: "Health, vitality, self-care", chooseTo: "I choose to exercise because I care about my health and energy" },
    { haveTo: "I have to call my mother", needs: "Connection, love, family harmony", chooseTo: "I choose to call my mother because I value our connection" },
    { haveTo: "I have to clean the house", needs: "Order, beauty, peace of mind", chooseTo: "I choose to clean because I need order and peace in my space" },
    { haveTo: "I have to pay bills", needs: "Security, integrity, responsibility", chooseTo: "I choose to pay bills because I value integrity and security" },
  ];

  // Timer effect
  useEffect(() => {
    let interval;
    if (timerActive && timerSeconds > 0) {
      interval = setInterval(() => {
        setTimerSeconds(prev => prev - 1);
      }, 1000);
    } else if (timerSeconds === 0) {
      setTimerActive(false);
      // Move to next item or complete
      if (currentItemIndex < 4) {
        setCurrentItemIndex(prev => prev + 1);
        setTimerSeconds(120);
      }
    }
    return () => clearInterval(interval);
  }, [timerActive, timerSeconds, currentItemIndex]);

  // Fullscreen toggle
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  const sections = [
    { id: 'intro', title: 'The Power of Choice', page: 155 },
    { id: 'why', title: 'Why This Matters', page: 155 },
    { id: 'process', title: 'The 3-Step Process', page: 155 },
    { id: 'example', title: 'See It In Action', page: 155 },
    { id: 'guided', title: 'Guided Practice', page: 155 },
    { id: 'interactive', title: 'Your Turn', page: 155 },
    { id: 'broadcast', title: 'Group Exercise Mode', page: 155 },
  ];

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const updateItem = (index, field, value) => {
    setHaveToItems(prev => prev.map((item, i) => 
      i === index ? { ...item, [field]: value } : item
    ));
  };

  const renderSectionContent = () => {
    switch (currentSection) {
      case 0: // Intro
        return (
          <div className="max-w-4xl mx-auto space-y-8 animate-fadeIn">
            {/* Hero */}
            <div className="relative rounded-2xl overflow-hidden h-64 md:h-80">
              <img src={IMAGES.hero} alt="Freedom of choice" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/60 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-8">
                <div className="flex items-center gap-2 text-amber-400 mb-2">
                  <Heart className="w-5 h-5" />
                  <span className="text-sm font-medium">Chapter 8 • Page 155</span>
                </div>
                <h1 className="text-4xl md:text-5xl font-bold text-white mb-2">
                  The Power of Choice
                </h1>
                <p className="text-xl text-slate-300">
                  Transform "Have To" into "Choose To"
                </p>
              </div>
            </div>

            {/* Key insight */}
            <Card className="bg-gradient-to-br from-amber-900/40 to-orange-900/40 border-amber-500/30">
              <CardContent className="p-8">
                <blockquote className="text-2xl md:text-3xl font-serif text-amber-100 text-center leading-relaxed">
                  "When we speak in terms of 'have to,' we deny responsibility for our actions. 
                  When we speak in terms of 'choose to,' we reclaim our power."
                </blockquote>
                <p className="text-amber-300/80 text-center mt-4">— Marshall Rosenberg</p>
              </CardContent>
            </Card>

            {/* Three cards */}
            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-slate-800/60 border border-slate-700 rounded-xl p-6 text-center">
                <div className="w-16 h-16 bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                  <X className="w-8 h-8 text-red-400" />
                </div>
                <h3 className="text-lg font-bold text-white mb-2">"Have To"</h3>
                <p className="text-slate-400 text-sm">Creates resentment, victim mentality, disconnection from our needs</p>
              </div>
              <div className="bg-slate-800/60 border border-slate-700 rounded-xl p-6 text-center">
                <div className="w-16 h-16 bg-amber-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                  <RefreshCw className="w-8 h-8 text-amber-400" />
                </div>
                <h3 className="text-lg font-bold text-white mb-2">The Shift</h3>
                <p className="text-slate-400 text-sm">Recognize the needs being met by our choices</p>
              </div>
              <div className="bg-slate-800/60 border border-slate-700 rounded-xl p-6 text-center">
                <div className="w-16 h-16 bg-emerald-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Check className="w-8 h-8 text-emerald-400" />
                </div>
                <h3 className="text-lg font-bold text-white mb-2">"Choose To"</h3>
                <p className="text-slate-400 text-sm">Empowerment, connection to values, joyful contribution</p>
              </div>
            </div>
          </div>
        );

      case 1: // Why This Matters
        return (
          <div className="max-w-4xl mx-auto space-y-8 animate-fadeIn">
            <div className="text-center mb-8">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Why This Matters</h2>
              <p className="text-xl text-slate-300">The language we use shapes our experience</p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {/* Have To Side */}
              <div className="bg-gradient-to-br from-red-950/50 to-red-900/30 border border-red-500/30 rounded-2xl p-6">
                <h3 className="text-2xl font-bold text-red-300 mb-4 flex items-center gap-2">
                  <X className="w-6 h-6" /> "Have To" Language
                </h3>
                <ul className="space-y-3">
                  {[
                    "Creates a sense of obligation and burden",
                    "Disconnects us from our values and needs",
                    "Breeds resentment over time",
                    "Makes us feel like victims of circumstance",
                    "Depletes our energy and motivation"
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3 text-red-100/80">
                      <span className="text-red-400 mt-1">•</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Choose To Side */}
              <div className="bg-gradient-to-br from-emerald-950/50 to-emerald-900/30 border border-emerald-500/30 rounded-2xl p-6">
                <h3 className="text-2xl font-bold text-emerald-300 mb-4 flex items-center gap-2">
                  <Check className="w-6 h-6" /> "Choose To" Language
                </h3>
                <ul className="space-y-3">
                  {[
                    "Acknowledges our agency and freedom",
                    "Connects us to the needs being met",
                    "Transforms obligation into purpose",
                    "Empowers us as the author of our life",
                    "Generates energy and enthusiasm"
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3 text-emerald-100/80">
                      <span className="text-emerald-400 mt-1">•</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <Card className="bg-blue-900/20 border border-blue-500/30">
              <CardContent className="p-6">
                <p className="text-blue-100 text-lg text-center">
                  <strong>Remember:</strong> We always have a choice. Even when options seem limited, 
                  acknowledging that we're choosing connects us to our power and our values.
                </p>
              </CardContent>
            </Card>
          </div>
        );

      case 2: // The 3-Step Process
        return (
          <div className="max-w-4xl mx-auto space-y-8 animate-fadeIn">
            <div className="text-center mb-8">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">The 3-Step Process</h2>
              <p className="text-xl text-slate-300">Transform any "have to" into "choose to"</p>
            </div>

            <div className="space-y-6">
              {/* Step 1 */}
              <div className="bg-slate-800/80 border border-slate-600 rounded-2xl p-8 flex items-start gap-6">
                <div className="w-16 h-16 bg-amber-500 rounded-full flex items-center justify-center flex-shrink-0 text-2xl font-bold text-slate-900">
                  1
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-amber-400 mb-2">Identify the "Have To"</h3>
                  <p className="text-slate-300 text-lg mb-3">
                    Write down something you tell yourself you "have to," "should," "must," or "ought to" do.
                  </p>
                  <div className="bg-slate-900/50 rounded-lg p-4 border border-slate-700">
                    <p className="text-slate-400 italic">"I have to go to work tomorrow."</p>
                  </div>
                </div>
              </div>

              {/* Step 2 */}
              <div className="bg-slate-800/80 border border-slate-600 rounded-2xl p-8 flex items-start gap-6">
                <div className="w-16 h-16 bg-amber-500 rounded-full flex items-center justify-center flex-shrink-0 text-2xl font-bold text-slate-900">
                  2
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-amber-400 mb-2">Discover the Needs</h3>
                  <p className="text-slate-300 text-lg mb-3">
                    Ask: "What needs of mine are met by doing this?" List all the needs you can identify.
                  </p>
                  <div className="bg-slate-900/50 rounded-lg p-4 border border-slate-700">
                    <p className="text-slate-400 italic">Security, contribution, purpose, connection with colleagues, financial stability</p>
                  </div>
                </div>
              </div>

              {/* Step 3 */}
              <div className="bg-slate-800/80 border border-slate-600 rounded-2xl p-8 flex items-start gap-6">
                <div className="w-16 h-16 bg-amber-500 rounded-full flex items-center justify-center flex-shrink-0 text-2xl font-bold text-slate-900">
                  3
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-amber-400 mb-2">Reframe with "Choose To"</h3>
                  <p className="text-slate-300 text-lg mb-3">
                    Rewrite using: "I choose to ___ because I value/need ___"
                  </p>
                  <div className="bg-emerald-900/30 rounded-lg p-4 border border-emerald-500/30">
                    <p className="text-emerald-200 italic">"I choose to go to work because I value security and making a meaningful contribution."</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 3: // See It In Action (Examples)
        return (
          <div className="max-w-5xl mx-auto space-y-8 animate-fadeIn">
            <div className="text-center mb-8">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">See It In Action</h2>
              <p className="text-xl text-slate-300">Real examples of transformation</p>
            </div>

            <div className="space-y-4">
              {exampleItems.map((item, index) => (
                <div key={index} className="bg-slate-800/60 border border-slate-700 rounded-xl overflow-hidden">
                  <div className="grid md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-slate-700">
                    {/* Have To */}
                    <div className="p-4 bg-red-950/20">
                      <div className="text-xs text-red-400 uppercase tracking-wide mb-1 font-medium">Have To</div>
                      <p className="text-red-100">{item.haveTo}</p>
                    </div>
                    {/* Needs */}
                    <div className="p-4 bg-amber-950/20">
                      <div className="text-xs text-amber-400 uppercase tracking-wide mb-1 font-medium">Needs Met</div>
                      <p className="text-amber-100">{item.needs}</p>
                    </div>
                    {/* Choose To */}
                    <div className="p-4 bg-emerald-950/20">
                      <div className="text-xs text-emerald-400 uppercase tracking-wide mb-1 font-medium">Choose To</div>
                      <p className="text-emerald-100">{item.chooseTo}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      case 4: // Guided Practice with Timer
        return (
          <div className="max-w-4xl mx-auto space-y-8 animate-fadeIn">
            <div className="text-center mb-8">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Guided Practice</h2>
              <p className="text-xl text-slate-300">2 minutes per item • Follow the timer</p>
            </div>

            {/* Timer Display */}
            <div className="bg-slate-800/80 border border-slate-600 rounded-2xl p-8 text-center">
              <div className="flex items-center justify-center gap-4 mb-6">
                <Timer className="w-8 h-8 text-amber-400" />
                <span className="text-6xl font-mono font-bold text-white">{formatTime(timerSeconds)}</span>
              </div>
              
              <div className="flex justify-center gap-4 mb-6">
                <Button 
                  onClick={() => setTimerActive(!timerActive)}
                  className={timerActive ? "bg-red-600 hover:bg-red-500" : "bg-emerald-600 hover:bg-emerald-500"}
                >
                  {timerActive ? <><Pause className="w-4 h-4 mr-2" /> Pause</> : <><Play className="w-4 h-4 mr-2" /> Start</>}
                </Button>
                <Button 
                  onClick={() => { setTimerSeconds(120); setTimerActive(false); }}
                  variant="outline" 
                  className="border-slate-600 text-white hover:bg-slate-800"
                >
                  <RotateCcw className="w-4 h-4 mr-2" /> Reset
                </Button>
              </div>

              {/* Progress */}
              <div className="flex justify-center gap-2 mb-6">
                {[0, 1, 2, 3, 4].map(i => (
                  <div
                    key={i}
                    className={`w-4 h-4 rounded-full transition-all ${
                      i === currentItemIndex ? 'bg-amber-500 scale-125' :
                      i < currentItemIndex ? 'bg-emerald-500' : 'bg-slate-700'
                    }`}
                  />
                ))}
              </div>

              <p className="text-slate-300">
                Currently on item <span className="text-amber-400 font-bold">{currentItemIndex + 1}</span> of 5
              </p>
            </div>

            {/* Current Item */}
            <Card className="bg-slate-800/60 border-slate-700">
              <CardContent className="p-6 space-y-4">
                <div className="text-center text-amber-400 font-medium mb-4">
                  Item {currentItemIndex + 1}: What's something you "have to" do?
                </div>
                
                <div className="space-y-4">
                  <div>
                    <label className="text-sm text-red-400 font-medium block mb-1">I have to...</label>
                    <Textarea 
                      value={haveToItems[currentItemIndex].haveTo}
                      onChange={(e) => updateItem(currentItemIndex, 'haveTo', e.target.value)}
                      placeholder="Write what you tell yourself you 'have to' do..."
                      className="bg-slate-900/50 border-slate-700 text-white"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-amber-400 font-medium block mb-1">Needs met by doing this:</label>
                    <Textarea 
                      value={haveToItems[currentItemIndex].needs}
                      onChange={(e) => updateItem(currentItemIndex, 'needs', e.target.value)}
                      placeholder="What needs are met? (security, connection, purpose...)"
                      className="bg-slate-900/50 border-slate-700 text-white"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-emerald-400 font-medium block mb-1">I choose to... because I value...</label>
                    <Textarea 
                      value={haveToItems[currentItemIndex].chooseTo}
                      onChange={(e) => updateItem(currentItemIndex, 'chooseTo', e.target.value)}
                      placeholder="Reframe using 'choose to' language..."
                      className="bg-slate-900/50 border-slate-700 text-white"
                    />
                  </div>
                </div>

                <div className="flex justify-between pt-4">
                  <Button 
                    onClick={() => setCurrentItemIndex(Math.max(0, currentItemIndex - 1))}
                    disabled={currentItemIndex === 0}
                    variant="outline"
                    className="border-slate-600 text-white hover:bg-slate-800"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" /> Previous
                  </Button>
                  <Button 
                    onClick={() => {
                      if (currentItemIndex < 4) {
                        setCurrentItemIndex(currentItemIndex + 1);
                        setTimerSeconds(120);
                      }
                    }}
                    disabled={currentItemIndex === 4}
                    className="bg-amber-600 hover:bg-amber-500"
                  >
                    Next <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        );

      case 5: // Your Turn (Interactive self-paced)
        return (
          <div className="max-w-5xl mx-auto space-y-8 animate-fadeIn">
            <div className="text-center mb-8">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Your Turn</h2>
              <p className="text-xl text-slate-300">Complete all 5 items at your own pace</p>
            </div>

            <div className="space-y-4">
              {haveToItems.map((item, index) => (
                <div key={item.id} className="bg-slate-800/60 border border-slate-700 rounded-xl overflow-hidden">
                  <div className="bg-slate-900/50 px-4 py-2 border-b border-slate-700 flex items-center justify-between">
                    <span className="text-amber-400 font-medium">Item {index + 1}</span>
                    {item.haveTo && item.needs && item.chooseTo && (
                      <Check className="w-5 h-5 text-emerald-400" />
                    )}
                  </div>
                  <div className="grid md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-slate-700">
                    <div className="p-4">
                      <label className="text-xs text-red-400 uppercase tracking-wide mb-2 block font-medium">I have to...</label>
                      <Textarea 
                        value={item.haveTo}
                        onChange={(e) => updateItem(index, 'haveTo', e.target.value)}
                        placeholder="What do you 'have to' do?"
                        className="bg-slate-900/50 border-slate-700 text-white text-sm min-h-[80px]"
                      />
                    </div>
                    <div className="p-4">
                      <label className="text-xs text-amber-400 uppercase tracking-wide mb-2 block font-medium">Needs Met</label>
                      <Textarea 
                        value={item.needs}
                        onChange={(e) => updateItem(index, 'needs', e.target.value)}
                        placeholder="What needs are met?"
                        className="bg-slate-900/50 border-slate-700 text-white text-sm min-h-[80px]"
                      />
                    </div>
                    <div className="p-4">
                      <label className="text-xs text-emerald-400 uppercase tracking-wide mb-2 block font-medium">I choose to...</label>
                      <Textarea 
                        value={item.chooseTo}
                        onChange={(e) => updateItem(index, 'chooseTo', e.target.value)}
                        placeholder="Reframe with 'choose to'..."
                        className="bg-slate-900/50 border-slate-700 text-white text-sm min-h-[80px]"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Summary */}
            <Card className="bg-emerald-900/20 border border-emerald-500/30">
              <CardContent className="p-6 text-center">
                <p className="text-emerald-200">
                  <strong>Completed:</strong> {haveToItems.filter(i => i.haveTo && i.needs && i.chooseTo).length} of 5 items
                </p>
              </CardContent>
            </Card>
          </div>
        );

      case 6: // Group Exercise / Broadcast Mode
        return (
          <div className={`animate-fadeIn ${isPresentationMode ? 'p-8' : 'max-w-5xl mx-auto space-y-8'}`}>
            {/* Controls */}
            {!isPresentationMode && (
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h2 className="text-3xl md:text-4xl font-bold text-white mb-2">Group Exercise Mode</h2>
                  <p className="text-xl text-slate-300">Perfect for Zoom broadcast or classroom display</p>
                </div>
                <div className="flex gap-2">
                  <Button 
                    onClick={() => setIsPresentationMode(true)}
                    className="bg-amber-600 hover:bg-amber-500"
                  >
                    <Maximize2 className="w-4 h-4 mr-2" /> Enter Presentation Mode
                  </Button>
                  <Button 
                    onClick={toggleFullscreen}
                    variant="outline"
                    className="border-slate-600 text-white hover:bg-slate-800"
                  >
                    {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
            )}

            {isPresentationMode ? (
              // Full Presentation View
              <div className="min-h-screen bg-slate-900 flex flex-col">
                <div className="flex justify-between items-center p-4 bg-slate-800/50">
                  <h1 className="text-2xl font-bold text-amber-400">
                    🌳 "Have To" → "Choose To" Exercise
                  </h1>
                  <Button 
                    onClick={() => setIsPresentationMode(false)}
                    variant="outline"
                    className="border-slate-600 text-white hover:bg-slate-800"
                  >
                    <Minimize2 className="w-4 h-4 mr-2" /> Exit Presentation
                  </Button>
                </div>

                <div className="flex-1 flex items-center justify-center p-8">
                  <div className="w-full max-w-6xl">
                    {/* Large display card */}
                    <div className="bg-slate-800/80 border-2 border-amber-500/50 rounded-3xl p-12 shadow-2xl">
                      <div className="text-center mb-8">
                        <p className="text-2xl text-slate-300 mb-4">Think of something you tell yourself you "have to" do...</p>
                        <p className="text-6xl font-bold text-white mb-8">What needs does it meet?</p>
                      </div>

                      <div className="grid grid-cols-3 gap-8 text-center">
                        <div className="bg-red-950/30 border border-red-500/30 rounded-2xl p-8">
                          <div className="text-red-400 text-xl mb-4">Step 1</div>
                          <p className="text-3xl text-white font-semibold">"I have to..."</p>
                          <p className="text-slate-400 mt-2">Identify the obligation</p>
                        </div>
                        <div className="bg-amber-950/30 border border-amber-500/30 rounded-2xl p-8">
                          <div className="text-amber-400 text-xl mb-4">Step 2</div>
                          <p className="text-3xl text-white font-semibold">Discover Needs</p>
                          <p className="text-slate-400 mt-2">What needs are met?</p>
                        </div>
                        <div className="bg-emerald-950/30 border border-emerald-500/30 rounded-2xl p-8">
                          <div className="text-emerald-400 text-xl mb-4">Step 3</div>
                          <p className="text-3xl text-white font-semibold">"I choose to..."</p>
                          <p className="text-slate-400 mt-2">Reclaim your power</p>
                        </div>
                      </div>

                      <div className="mt-12 text-center">
                        <p className="text-2xl text-amber-300 italic">
                          "Every action is an attempt to meet a need."
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Bottom instruction */}
                <div className="p-4 bg-slate-800/50 text-center">
                  <p className="text-slate-400">Take 2 minutes to reflect on each item • Share with your partner or write in your journal</p>
                </div>
              </div>
            ) : (
              // Normal group mode view
              <div className="space-y-6">
                <Card className="bg-amber-900/20 border border-amber-500/30">
                  <CardContent className="p-6">
                    <h3 className="text-xl font-bold text-amber-300 mb-4">📺 How to Use for Broadcast:</h3>
                    <ol className="space-y-2 text-amber-100/80">
                      <li className="flex items-start gap-2">
                        <span className="text-amber-400 font-bold">1.</span>
                        Click "Enter Presentation Mode" for a clean, large-format display
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-amber-400 font-bold">2.</span>
                        Share your screen on Zoom or project to classroom
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-amber-400 font-bold">3.</span>
                        Guide participants through the 3-step process
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-amber-400 font-bold">4.</span>
                        Give 2 minutes per item for reflection and writing
                      </li>
                    </ol>
                  </CardContent>
                </Card>

                {/* Preview of presentation */}
                <div className="bg-slate-800/60 border border-slate-700 rounded-xl p-6">
                  <h3 className="text-lg font-bold text-white mb-4">Preview:</h3>
                  <div className="aspect-video bg-slate-900 rounded-lg flex items-center justify-center border border-slate-700">
                    <div className="text-center">
                      <p className="text-4xl font-bold text-white mb-2">Transform "Have To" → "Choose To"</p>
                      <p className="text-xl text-slate-400">Click Presentation Mode to begin</p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className={isPresentationMode ? "min-h-screen bg-slate-900" : "min-h-screen bg-slate-900"}>
      {/* Header - hide in presentation mode */}
      {!isPresentationMode && (
        <div className="bg-slate-800/50 border-b border-slate-700 sticky top-0 z-50 backdrop-blur-sm">
          <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
            <button 
              onClick={() => navigate('/belonging-ebook')}
              className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Back to E-Book</span>
            </button>
            
            <div className="flex items-center gap-2">
              <span className="text-amber-400 font-medium">Chapter 8</span>
              <span className="text-slate-500">•</span>
              <span className="text-slate-300">Page 155</span>
            </div>

            <div className="flex items-center gap-2">
              <Button
                onClick={toggleFullscreen}
                variant="outline"
                size="sm"
                className="border-slate-600 text-slate-300 hover:bg-slate-800"
              >
                {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Navigation Pills - hide in presentation mode */}
      {!isPresentationMode && (
        <div className="bg-slate-900/50 border-b border-slate-800 overflow-x-auto">
          <div className="max-w-7xl mx-auto px-4 py-2 flex gap-2">
            {sections.map((section, index) => (
              <button
                key={section.id}
                onClick={() => setCurrentSection(index)}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                  currentSection === index
                    ? 'bg-amber-500 text-slate-900'
                    : 'bg-slate-800 text-slate-400 hover:bg-slate-700 hover:text-white'
                }`}
              >
                {section.title}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Content */}
      <div className={isPresentationMode ? "" : "py-8 px-4"}>
        {renderSectionContent()}
      </div>

      {/* Footer Navigation - hide in presentation mode */}
      {!isPresentationMode && (
        <div className="fixed bottom-0 left-0 right-0 bg-slate-900/95 border-t border-slate-800 backdrop-blur-sm">
          <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
            <Button
              onClick={() => setCurrentSection(Math.max(0, currentSection - 1))}
              disabled={currentSection === 0}
              variant="outline"
              className="border-slate-700 text-white hover:bg-slate-800"
            >
              <ArrowLeft className="w-4 h-4 mr-2" /> Previous
            </Button>

            <div className="flex items-center gap-1">
              {sections.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentSection(index)}
                  className={`w-2 h-2 rounded-full transition-all ${
                    currentSection === index ? 'bg-amber-500 w-6' : 'bg-slate-700 hover:bg-slate-600'
                  }`}
                />
              ))}
            </div>

            <Button
              onClick={() => setCurrentSection(Math.min(sections.length - 1, currentSection + 1))}
              disabled={currentSection === sections.length - 1}
              className="bg-amber-600 hover:bg-amber-500 text-white"
            >
              Next <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      )}

      <style jsx>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn {
          animation: fadeIn 0.5s ease-out;
        }
      `}</style>
    </div>
  );
}
